/**
 * OutreachPage Component
 * 
 * Contact management and email drafts (AI generation in Phase 3).
 */

import { useEffect, useState } from 'react';
import { Send, Plus, Mail } from 'lucide-react';
import { useJobHuntStore } from '@/stores/jobHuntStore';
import { LoadingState } from '../shared/LoadingState';
import { EmptyState } from '../shared/EmptyState';
import { ContactCard } from './ContactCard';
import { ContactFormDialog } from './ContactFormDialog';
import { EmailGeneratorDialog } from './EmailGeneratorDialog';
import { EmailDraftCard } from './EmailDraftCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { CompanyContact } from '@/types/job-hunt';

export function OutreachPage() {
  const { contacts, outreachEmails, jobs, isLoading, fetchContacts, fetchOutreachEmails, fetchJobs } = useJobHuntStore();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CompanyContact | null>(null);
  const [isEmailGenDialogOpen, setIsEmailGenDialogOpen] = useState(false);
  const [selectedContactForEmail, setSelectedContactForEmail] = useState<string | null>(null);
  const { toast } = useToast();

  const openEmailGenerator = (contactId: string) => {
    setSelectedContactForEmail(contactId);
    setIsEmailGenDialogOpen(true);
  };

  const handleEmailGenerated = (subject: string, body: string) => {
    // Could save draft here in future
    console.log('Generated email:', { subject, body });
  };

  const handleDeleteEmail = async (emailId: string) => {
    try {
      const { error } = await supabase
        .from('outreach_emails')
        .delete()
        .eq('id', emailId);

      if (error) throw error;

      toast({
        title: "Email deleted",
        description: "The draft has been removed.",
      });

      fetchOutreachEmails();
    } catch (error) {
      console.error('Error deleting email:', error);
      toast({
        title: "Error",
        description: "Failed to delete email draft.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchOutreachEmails();
    fetchJobs();
  }, [fetchContacts, fetchOutreachEmails, fetchJobs]);

  if (isLoading) {
    return <LoadingState message="Loading outreach data..." />;
  }

  if (contacts.length === 0 && outreachEmails.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Outreach</h1>
          <p className="text-sm text-muted-foreground">
            Manage contacts and email templates
          </p>
        </div>

        <EmptyState
          icon={<Send className="h-6 w-6 text-muted-foreground" />}
          title="No Outreach Yet"
          description="Start building your network by adding contacts and creating personalized outreach emails."
          actionLabel="Add Contact"
          onAction={() => setIsAddDialogOpen(true)}
        />

        <ContactFormDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          onSuccess={() => fetchContacts()}
        />
      </div>
    );
  }

  const needsFollowup = contacts.filter(c => 
    c.next_followup_date && new Date(c.next_followup_date) <= new Date()
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold">Outreach</h1>
          <p className="text-sm text-muted-foreground">
            Manage contacts and networking activities
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add Contact
        </Button>
      </div>

      {/* Compact Stats Grid */}
      <div className="grid gap-3 grid-cols-3">
        {/* Total Contacts */}
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Contacts</p>
            <p className="text-3xl font-bold">{contacts.length}</p>
            <p className="text-xs text-muted-foreground">connections</p>
          </CardContent>
        </Card>

        {/* Email Drafts */}
        <Card className="bg-gradient-to-br from-accent/5 to-accent/10">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Email Drafts</p>
            <p className="text-3xl font-bold">{outreachEmails.length}</p>
            <p className="text-xs text-muted-foreground">templates</p>
          </CardContent>
        </Card>

        {/* Follow-ups */}
        <Card className="bg-gradient-to-br from-warning/5 to-warning/10">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Follow-ups</p>
            <p className="text-3xl font-bold">{needsFollowup.length}</p>
            <p className="text-xs text-muted-foreground">pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="emails" className="space-y-4">
        <TabsList>
          <TabsTrigger value="emails">
            Emails ({outreachEmails.length})
          </TabsTrigger>
          <TabsTrigger value="contacts">
            Contacts ({contacts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contacts" className="space-y-3">
          {needsFollowup.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Needs Follow-up</h3>
              <div className="space-y-2">
                {needsFollowup.map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onEdit={() => setEditingContact(contact)}
                    onGenerateEmail={() => openEmailGenerator(contact.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {contacts.filter(c => !needsFollowup.includes(c)).length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">All Contacts</h3>
              <div className="space-y-2">
                {contacts
                  .filter(c => !needsFollowup.includes(c))
                  .map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      onEdit={() => setEditingContact(contact)}
                      onGenerateEmail={() => openEmailGenerator(contact.id)}
                    />
                  ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="emails" className="space-y-3">
          {outreachEmails.length === 0 ? (
            <EmptyState
              icon={<Mail className="h-6 w-6 text-muted-foreground" />}
              title="No Email Drafts"
              description="Create email drafts by clicking 'Generate Email' on any contact card"
            />
          ) : (
            <div className="space-y-2">
              {outreachEmails.map((email) => {
                const jobListing = email.job_listing_id 
                  ? jobs.find(j => j.id === email.job_listing_id)
                  : undefined;
                const contact = email.contact_id
                  ? contacts.find(c => c.id === email.contact_id)
                  : undefined;

                return (
                  <EmailDraftCard
                    key={email.id}
                    email={email}
                    jobListing={jobListing}
                    contact={contact}
                    onDelete={handleDeleteEmail}
                  />
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ContactFormDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSuccess={() => fetchContacts()}
      />

      <ContactFormDialog
        open={!!editingContact}
        onOpenChange={(open) => !open && setEditingContact(null)}
        contact={editingContact || undefined}
        onSuccess={() => {
          fetchContacts();
          setEditingContact(null);
        }}
      />

      {selectedContactForEmail && (
        <EmailGeneratorDialog
          open={isEmailGenDialogOpen}
          onOpenChange={setIsEmailGenDialogOpen}
          contactId={selectedContactForEmail}
          onEmailGenerated={handleEmailGenerated}
        />
      )}
    </div>
  );
}
